import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Play, Pause, Captions, Zap } from "lucide-react";
import { LESSON_INTROS } from "@/data/lessonIntros";
import { useA11y } from "@/context/A11yContext";
import { useStemMix, MixSliders } from "@/components/CountAlongMixer";

const VIDEO_FOR = { 1: "lesson1s-intro", 21: "lesson1d-intro", 41: "lesson1g-intro", 61: "lesson1-intro", 2: "lesson2s-intro", 22: "lesson2d-intro", 42: "lesson2g-intro", 62: "lesson2-intro", 3: "lesson3-intro", 23: "lesson3-intro", 43: "lesson3-intro", 63: "lesson3-intro", 4: "lesson4-intro", 24: "lesson4-intro", 44: "lesson4-intro", 64: "lesson4-intro", 5: "lesson5s-intro", 25: "lesson5d-intro", 45: "lesson5g-intro", 65: "lesson5-intro", 6: "lesson6s-intro", 46: "lesson6g-intro", 66: "lesson6-intro", 7: "lesson7s-intro", 47: "lesson7g-intro", 67: "lesson7-intro", 27: "lesson7d-intro", 26: "lesson6d-intro", 8: "lesson8-intro", 48: "lesson8-intro", 68: "lesson8-intro", 28: "lesson8-intro", 9: "lesson9s-intro", 49: "lesson9g-intro", 69: "lesson9-intro", 29: "lesson9d-intro", 10: "lesson10s-intro", 50: "lesson10g-intro", 70: "lesson10-intro", 30: "lesson10d-intro" };

const MEDIA_V = "20261102i";
const SPEEDS = [1, 1.25, 1.5];
const videoKey = (name) => (name.includes("timesigs") ? "2/2 and 6/8" : `Lesson ${name.match(/\d+/)[0]}`);
const watched = (v) => { let s = 0; for (let i = 0; i < v.played.length; i++) s += v.played.end(i) - v.played.start(i); return s; };

// Award one star the first time a learner watches a count-along video all the way through.
const earnStar = (v, name, user, challenge = false) => {
  if (!user || !v.duration || watched(v) < v.duration * 0.9) return;
  api.post("/me/perfect", { game: `Count along ${challenge ? "challenge " : ""}· ${videoKey(name)}` }).then(({ data }) => {
    if (!data.added) return;
    const tier = { 10: "bronze", 50: "silver", 100: "gold" }[data.stars];
    const done = challenge ? "Challenge complete!" : "Count-along finished!";
    toast.success(tier ? `You've won the ${tier} trophy for ${data.stars} stars!` : `${done} You earned a star (${data.stars} in total)`);
    if (data.speed_counter) toast.success("You've won the Speed Counter badge! Every challenge done at 110 beats a minute.", { duration: 8000 });
  }).catch(() => {});
};

// Optional third go: every exercise once more at 110 beats a minute.
const Challenge = ({ name, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-primary/20 px-4 py-3" data-testid={`${testId}-challenge`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testId}-challenge-toggle`}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105">
        <Zap size={15} /> {open ? "Hide the challenge" : "Take the challenge · 110 beats a minute"}
      </button>
      {open && <ChallengePlayer name={name} testId={testId} />}
    </div>
  );
};

const CountAlongMix = ({ videoRef, name, testId }) => {
  const mix = useStemMix(videoRef, name, MEDIA_V);
  return <MixSliders mix={mix} testId={testId} />;
};

const ChallengePlayer = ({ name, testId }) => {
  const { user } = useAuth();
  const { captions } = useA11y();
  const ref = useRef(null);
  const c = `${name}-challenge`;
  return (
    <>
      <video ref={ref} onEnded={(e) => earnStar(e.currentTarget, name, user, true)} controls playsInline preload="metadata" poster={`/media/intros/${c}.jpg?v=${MEDIA_V}`} className="mt-3 w-full rounded-xl" data-testid={`${testId}-challenge-player`}>
        <source src={`/media/intros/${c}.mp4?v=${MEDIA_V}`} type="video/mp4" />
        <track kind="captions" src={`/media/intros/${c}.vtt?v=${MEDIA_V}`} srcLang="en" label="English" default={captions} />
      </video>
      <CountAlongMix videoRef={ref} name={c} testId={`${testId}-challenge`} />
    </>
  );
};

const Chapters = ({ name, videoRef, testId }) => {
  const [list, setList] = useState([]);
  useEffect(() => { fetch(`/media/intros/${name}.chapters.json?v=${MEDIA_V}`).then((r) => (r.ok ? r.json() : [])).then(setList).catch(() => setList([])); }, [name]);
  if (list.length < 2) return null;
  const go = (t) => { const v = videoRef.current; if (!v) return; v.currentTime = t; v.play().catch(() => {}); };
  return (
    <div className="flex flex-wrap items-center gap-2 px-4 pt-3" data-testid={`${testId}-chapters`}>
      <span className="text-sm text-muted-foreground">Jump to:</span>
      {list.map((c) => (
        <button key={c.n} type="button" onClick={() => go(c.t)} data-testid={`${testId}-chapter-${c.n}`}
          className="rounded-full border border-border px-3 py-1 text-xs font-semibold transition-colors hover:border-primary hover:text-primary">{c.n}. {c.title}</button>
      ))}
    </div>
  );
};
export const SummaryVideo = ({ name, label = "Summary of lesson video · under 1 min", testId = "lesson1-intro-video", speeds = false }) => {
  const { captions } = useA11y();
  const { user } = useAuth();
  const ref = useRef(null);
  const [rate, setRate] = useState(1);
  const ca = speeds && /countalong|timesigs/.test(name);
  const pick = (r) => { setRate(r); if (ref.current) { ref.current.preservesPitch = true; ref.current.playbackRate = r; } };
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-primary/30 bg-primary/5" data-testid={testId}>
      <p className="px-4 pt-3 font-mono text-[11px] uppercase tracking-widest text-primary">{label}</p>
      <video ref={ref} onLoadedMetadata={(e) => { e.currentTarget.playbackRate = rate; }} onEnded={ca ? (e) => earnStar(e.currentTarget, name, user) : undefined} controls playsInline preload="metadata" poster={`/media/intros/${name}.jpg?v=${MEDIA_V}`} className="mt-2 w-full" data-testid={`${testId}-player`}>
        <source src={`/media/intros/${name}.mp4?v=${MEDIA_V}`} type="video/mp4" />
        <track kind="captions" src={`/media/intros/${name}.vtt?v=${MEDIA_V}`} srcLang="en" label="English" default={captions} />
      </video>
      {ca && <CountAlongMix videoRef={ref} name={name} testId={testId} />}
      {ca && <Chapters name={name} videoRef={ref} testId={testId} />}
      {speeds && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-3" data-testid={`${testId}-speeds`}>
          <span className="text-sm text-muted-foreground">Try it faster:</span>
          {SPEEDS.map((r) => (
            <button key={r} type="button" onClick={() => pick(r)} data-testid={`${testId}-speed-${String(r).replace(".", "_")}`}
              className={`rounded-full px-3.5 py-1 text-sm font-semibold transition-colors ${rate === r ? "bg-primary text-primary-foreground" : "border border-border hover:border-primary"}`}>{r}×</button>
          ))}
        </div>
      )}
      {ca && <Challenge name={name} testId={testId} />}
    </div>
  );
};

const MASTERY_IDS = [...Array(10)].flatMap((_, i) => [11 + i, 31 + i, 51 + i, 71 + i]);
MASTERY_IDS.forEach((id) => { VIDEO_FOR[id] = `m${id}-intro`; });

export const LessonIntro = ({ lessonId }) => {
  if (VIDEO_FOR[lessonId]) return <SummaryVideo key={lessonId} name={VIDEO_FOR[lessonId]} label={MASTERY_IDS.includes(lessonId) ? "Summary of lesson video · about 1 min" : undefined} />;
  return <LessonAudioIntro lessonId={lessonId} />;
};

const LessonAudioIntro = ({ lessonId }) => {
  const text = LESSON_INTROS[String(lessonId)];
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const { captions, volume } = useA11y();
  if (!text) return null;
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (a.paused) { a.volume = Math.min(1, volume); a.play(); } else a.pause();
  };
  return (
    <div className="mt-6 flex flex-wrap items-center gap-4 rounded-2xl border border-primary/30 bg-primary/5 px-4 py-3" data-testid="lesson-intro">
      <audio ref={ref} src={`/media/intros/${lessonId}.mp3?v=${MEDIA_V}`} preload="none" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
      <button onClick={toggle} aria-label={playing ? "Pause lesson welcome" : "Play lesson welcome"} data-testid="lesson-intro-play"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105">
        {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="font-mono text-[11px] uppercase tracking-widest text-primary">Lesson welcome · 15 sec</p>
        {(captions || playing) && <p className="mt-0.5 flex gap-1.5 text-sm text-foreground" data-testid="lesson-intro-caption"><Captions size={14} className="mt-0.5 shrink-0 text-muted-foreground" aria-hidden="true" />{text}</p>}
      </div>
    </div>
  );
};
